<p-toast></p-toast>
<app-page-shell>
  <app-page-hero eyebrow="HR · Company Compliance" title="Admin Operations"
    subtitle="Control government portals, filings, registrations, licenses, inspections, renewals, evidence, and accountable ownership by company and site.">
    <div actions class="hero-actions">
      @if (canImportExport) {
        <button pButton type="button" label="Export {{ year }}" icon="pi pi-file-excel"
          severity="secondary" [outlined]="true" (click)="exportWorkbook()"></button>
      }
      @if (canManage && mutationAllowed) {
        <button pButton type="button" label="Evaluate" icon="pi pi-bolt"
          severity="secondary" [outlined]="true" (click)="evaluate()"></button>
      }
      <button pButton type="button" label="Refresh" icon="pi pi-refresh" (click)="load()"></button>
    </div>
  </app-page-hero>

  @if (!mutationAllowed) {
    <div class="scope-notice">
      <i class="fa-solid fa-layer-group"></i>
      <div><strong>Group reporting view</strong><span>Select a member company to create, edit, verify, import, or activate templates.</span></div>
    </div>
  }

  @if (loading) {
    <p-progressBar mode="indeterminate"></p-progressBar>
  }

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <p-tabs [(value)]="activeTab" styleClass="admin-ops-tabs">
    <p-tablist>
      <p-tab value="overview"><i class="fa-solid fa-gauge-high"></i> Overview</p-tab>
      <p-tab value="obligations"><i class="fa-solid fa-list-check"></i> Obligations</p-tab>
      <p-tab value="calendar"><i class="fa-solid fa-calendar-days"></i> Annual Calendar</p-tab>
      <p-tab value="documents"><i class="fa-solid fa-file-shield"></i> Documents &amp; Licenses</p-tab>
      <p-tab value="portals"><i class="fa-solid fa-arrow-up-right-from-square"></i> Portal Directory</p-tab>
      <p-tab value="setup"><i class="fa-solid fa-sliders"></i> Setup</p-tab>
    </p-tablist>

    <p-tabpanels>
      <p-tabpanel value="overview">
        <section class="overview-grid">
          <app-page-data-panel title="Immediate Attention"
            subtitle="Open compliance work sorted by due-date pressure."
            [resultText]="(dashboard.attentionCycles?.length || 0) + ' cycles'">
            <p-table [value]="dashboard.attentionCycles || []" styleClass="p-datatable-sm"
              responsiveLayout="scroll" [tableStyle]="{ 'min-width': '780px' }">
              <ng-template pTemplate="header">
                <tr><th>Requirement</th><th>Scope</th><th>Owner</th><th>Due</th><th>Risk</th><th></th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-cycle>
                <tr>
                  <td><strong>{{ cycle.obligationName }}</strong><br><small>{{ cycle.authorityName || cycle.obligationCode }}</small></td>
                  <td>{{ cycle.scopeName || '-' }}</td>
                  <td>{{ cycle.ownerName || 'Unassigned' }}</td>
                  <td>{{ cycle.dueDate | date:'mediumDate' }}<br><small>{{ daysLabel(cycle.daysUntilDue) }}</small></td>
                  <td><p-tag [value]="format(cycle.riskStatus || cycle.workflowStatus)"
                    [severity]="statusSeverity(cycle.riskStatus || cycle.workflowStatus)"></p-tag></td>
                  <td>
                    @if (canAct) {
                      <button pButton type="button" icon="pi pi-arrow-right" [rounded]="true" [text]="true"
                        aria-label="Open compliance action" (click)="openAction(cycle)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage"><tr><td colspan="6">No compliance cycles need attention.</td></tr></ng-template>
            </p-table>
          </app-page-data-panel>

          <app-page-data-panel title="Expiring Documents"
            subtitle="Company documents and licenses approaching or beyond expiry."
            [resultText]="(dashboard.attentionDocuments?.length || 0) + ' documents'">
            <p-table [value]="dashboard.attentionDocuments || []" styleClass="p-datatable-sm"
              responsiveLayout="scroll" [tableStyle]="{ 'min-width': '680px' }">
              <ng-template pTemplate="header">
                <tr><th>Document</th><th>Expiry</th><th>Status</th><th>Verification</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-document>
                <tr>
                  <td><strong>{{ document.documentName }}</strong><br><small>{{ document.documentNumber || '-' }}</small></td>
                  <td>{{ document.expiryDate | date:'mediumDate' }}<br><small>{{ daysLabel(document.daysLeft) }}</small></td>
                  <td><p-tag [value]="format(document.documentStatus)" [severity]="statusSeverity(document.documentStatus)"></p-tag></td>
                  <td><p-tag [value]="format(document.verificationStatus)" [severity]="statusSeverity(document.verificationStatus)"></p-tag></td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage"><tr><td colspan="4">No document expiry risks found.</td></tr></ng-template>
            </p-table>
          </app-page-data-panel>
        </section>

        <section class="breakdown-grid">
          <app-page-data-panel title="Risk by Entity / Site" subtitle="Open and overdue workload by compliance scope.">
            <div class="rank-list">
              @for (item of dashboard.riskByScope || []; track item.key) {
                <div><span><strong>{{ item.label }}</strong><small>{{ item.total || 0 }} open</small></span>
                  <p-tag [value]="(item.overdue || 0) + ' overdue'" [severity]="item.overdue ? 'danger' : 'success'"></p-tag></div>
              } @empty { <p class="empty-copy">No scoped risk recorded.</p> }
            </div>
          </app-page-data-panel>
          <app-page-data-panel title="Risk by Category" subtitle="Exposure across labor, logistics, facilities, tax, and corporate controls.">
            <div class="rank-list">
              @for (item of dashboard.riskByCategory || []; track item.key) {
                <div><span><strong>{{ format(item.label) }}</strong><small>{{ item.total || 0 }} open</small></span>
                  <p-tag [value]="(item.dueSoon || 0) + ' due soon'" [severity]="item.dueSoon ? 'warn' : 'success'"></p-tag></div>
              } @empty { <p class="empty-copy">No category risk recorded.</p> }
            </div>
          </app-page-data-panel>
          <app-page-data-panel title="Owner Workload" subtitle="Open work assigned to accountable employees.">
            <div class="rank-list">
              @for (item of dashboard.ownerWorkload || []; track item.key) {
                <div><span><strong>{{ item.label || 'Unassigned' }}</strong><small>{{ item.total || 0 }} open</small></span>
                  <p-tag [value]="(item.overdue || 0) + ' overdue'" [severity]="item.overdue ? 'danger' : 'success'"></p-tag></div>
              } @empty { <p class="empty-copy">No owner workload recorded.</p> }
            </div>
          </app-page-data-panel>
        </section>
      </p-tabpanel>

      <p-tabpanel value="obligations">
        <app-page-data-panel title="Obligation Register"
          subtitle="Recurring and as-needed portal visits, filings, registrations, renewals, inspections, memberships, and contracts."
          [resultText]="filteredObligations.length + ' obligations'">
          @if (canManage && mutationAllowed) {
            <div actions><button pButton type="button" label="New Obligation" icon="pi pi-plus" (click)="editObligation()"></button></div>
          }
          <div class="filter-grid">
            <p-select [options]="scopes" optionLabel="scopeName" optionValue="scopeId" [(ngModel)]="siteFilter"
              placeholder="All sites" [showClear]="true"></p-select>
            <p-select [options]="authorities" optionLabel="authorityName" optionValue="authorityId" [(ngModel)]="authorityFilter"
              placeholder="All authorities" [showClear]="true"></p-select>
            <p-select [options]="ownerOptions" [(ngModel)]="ownerFilter" placeholder="All owners" [showClear]="true"></p-select>
            <p-select [options]="frequencyOptions" [(ngModel)]="frequencyFilter" placeholder="All frequencies" [showClear]="true"></p-select>
            <button pButton type="button" label="Clear" icon="pi pi-filter-slash" severity="secondary"
              [outlined]="true" (click)="clearFilters()"></button>
          </div>
          <p-table [value]="filteredObligations" [loading]="loading" styleClass="p-datatable-sm"
            responsiveLayout="scroll" [paginator]="true" [rows]="20" [tableStyle]="{ 'min-width': '1120px' }">
            <ng-template pTemplate="header">
              <tr><th>Requirement</th><th>Purpose</th><th>Scope</th><th>Authority</th><th>Frequency</th><th>Owner</th><th>Controls</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><strong>{{ item.obligationName }}</strong><br><small>{{ item.obligationCode }} · {{ format(item.obligationType) }}</small></td>
                <td>{{ item.purpose || '-' }}</td>
                <td>{{ item.scopeName || '-' }}</td>
                <td>{{ item.authorityName || '-' }}
                  @if (item.portalUrl) { <br><a [href]="item.portalUrl" target="_blank" rel="noopener">Open portal</a> }
                </td>
                <td><p-tag [value]="format(item.frequencyCode)" severity="info"></p-tag><br><small>{{ item.firstDueDate || 'As needed' }}</small></td>
                <td>{{ item.ownerName || 'Unassigned' }}</td>
                <td><small>{{ item.evidenceRequired ? 'Evidence' : 'No evidence' }} · {{ item.approvalRequired ? 'Approval' : 'Direct completion' }}</small></td>
                <td>
                  @if (canManage && mutationAllowed) {
                    <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true"
                      aria-label="Edit obligation" (click)="editObligation(item)"></button>
                  }
                  @if (canComplete && item.frequencyCode === 'AS_NEEDED') {
                    <button pButton type="button" icon="pi pi-play" [rounded]="true" [text]="true"
                      aria-label="Launch as-needed cycle" (click)="launchAsNeeded(item)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage"><tr><td colspan="8">No obligations match the selected filters.</td></tr></ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Compliance Cycle Register"
          subtitle="Filter generated work by site, category, owner, workflow status, and due-date range."
          [resultText]="filteredCycles.length + ' cycles'">
          <div class="filter-grid cycle-filters">
            <p-select [options]="scopes" optionLabel="scopeName" optionValue="scopeId" [(ngModel)]="siteFilter"
              placeholder="All sites" [showClear]="true"></p-select>
            <p-select [options]="categoryOptions" [(ngModel)]="categoryFilter"
              placeholder="All categories" [showClear]="true"></p-select>
            <p-select [options]="ownerOptions" [(ngModel)]="ownerFilter"
              placeholder="All owners" [showClear]="true"></p-select>
            <p-select [options]="statusOptions" [(ngModel)]="statusFilter"
              placeholder="All workflow states" [showClear]="true"></p-select>
            <input pInputText type="date" aria-label="Due date from" [(ngModel)]="dateFrom">
            <input pInputText type="date" aria-label="Due date to" [(ngModel)]="dateTo">
            <button pButton type="button" label="Clear" icon="pi pi-filter-slash" severity="secondary"
              [outlined]="true" (click)="clearFilters()"></button>
          </div>
          <p-table [value]="filteredCycles" [loading]="loading" styleClass="p-datatable-sm"
            responsiveLayout="scroll" [paginator]="true" [rows]="20" [tableStyle]="{ 'min-width': '980px' }">
            <ng-template pTemplate="header">
              <tr><th>Requirement</th><th>Scope / Category</th><th>Owner</th><th>Due</th><th>Workflow</th><th>Risk</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-cycle>
              <tr>
                <td><strong>{{ cycle.obligationName }}</strong><br><small>{{ cycle.obligationCode }}</small></td>
                <td>{{ cycle.scopeName || '-' }}<br><small>{{ format(cycle.category) }}</small></td>
                <td>{{ cycle.ownerName || 'Unassigned' }}</td>
                <td>{{ cycle.dueDate | date:'mediumDate' }}<br><small>{{ daysLabel(cycle.daysUntilDue) }}</small></td>
                <td><p-tag [value]="format(cycle.workflowStatus)" [severity]="statusSeverity(cycle.workflowStatus)"></p-tag></td>
                <td><p-tag [value]="format(cycle.riskStatus)" [severity]="statusSeverity(cycle.riskStatus)"></p-tag></td>
                <td>@if (canAct) {
                  <button pButton type="button" icon="pi pi-arrow-right" [rounded]="true" [text]="true"
                    aria-label="Open compliance action" (click)="openAction(cycle)"></button>
                }</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage"><tr><td colspan="7">No compliance cycles match the selected filters.</td></tr></ng-template>
          </p-table>
        </app-page-data-panel>
      </p-tabpanel>

      <p-tabpanel value="calendar">
        <app-page-data-panel title="Annual Compliance Matrix"
          subtitle="Unscheduled months are N/A. Scheduled months show the current workflow or calculated risk state."
          [resultText]="(calendar.rows?.length || 0) + ' requirements'">
          <div actions class="calendar-actions">
            <button pButton type="button" icon="pi pi-chevron-left" [rounded]="true" [text]="true" (click)="changeYear(-1)"></button>
            <strong>{{ year }}</strong>
            <button pButton type="button" icon="pi pi-chevron-right" [rounded]="true" [text]="true" (click)="changeYear(1)"></button>
          </div>
          <div class="matrix-wrap">
            <table class="annual-matrix">
              <thead><tr><th>Obligation</th><th>Scope</th><th>Frequency</th>
                @for (month of months; track month.key) { <th>{{ month.label }}</th> }
              </tr></thead>
              <tbody>
                @for (row of calendar.rows || []; track row.obligationId) {
                  <tr>
                    <td><strong>{{ row.obligationName }}</strong><small>{{ row.authorityName || '-' }}</small></td>
                    <td>{{ row.scopeName || '-' }}</td>
                    <td>{{ format(row.frequencyCode) }}</td>
                    @for (month of months; track month.key) {
                      @let cycle = cycleFor(row, month.key);
                      <td class="month-cell" [class.is-actionable]="!!cycle && canAct"
                        (click)="cycle && canAct ? openAction(cycle) : null">
                        <p-tag [value]="format(cycleCellStatus(row, month.key))"
                          [severity]="statusSeverity(cycleCellStatus(row, month.key))"></p-tag>
                      </td>
                    }
                  </tr>
                } @empty { <tr><td colspan="15">No scheduled compliance rows for {{ year }}.</td></tr> }
              </tbody>
            </table>
          </div>
        </app-page-data-panel>
      </p-tabpanel>

      <p-tabpanel value="documents">
        <app-page-data-panel title="Company Documents & Licenses"
          subtitle="Versioned registrations, certificates, licenses, leases, and evidence with calculated expiry status."
          [resultText]="documents.length + ' documents'">
          @if (canManage && mutationAllowed) {
            <div actions><button pButton type="button" label="Add Document" icon="pi pi-plus" (click)="editDocument()"></button></div>
          }
          <p-table [value]="documents" [loading]="loading" styleClass="p-datatable-sm"
            responsiveLayout="scroll" [paginator]="true" [rows]="20" [tableStyle]="{ 'min-width': '1060px' }">
            <ng-template pTemplate="header">
              <tr><th>Document</th><th>Requirement</th><th>Number / Issuer</th><th>Expiry</th><th>Status</th><th>Verification</th><th>File</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><strong>{{ item.documentName }}</strong><br><small>Version {{ item.previousDocumentId ? 'renewed' : 'original' }}</small></td>
                <td>{{ item.obligationName || '-' }}</td>
                <td>{{ item.documentNumber || '-' }}<br><small>{{ item.issuer || '-' }}</small></td>
                <td>{{ item.expiryDate | date:'mediumDate' }}<br><small>{{ daysLabel(item.daysLeft) }}</small></td>
                <td><p-tag [value]="format(item.documentStatus)" [severity]="statusSeverity(item.documentStatus)"></p-tag></td>
                <td><p-tag [value]="format(item.verificationStatus)" [severity]="statusSeverity(item.verificationStatus)"></p-tag></td>
                <td>
                  @if (item.fileUrl) { <a [href]="item.fileUrl" target="_blank" rel="noopener">Preview</a> }
                  @else { <small>No file</small> }
                </td>
                <td class="row-actions">
                  @if (canManage && mutationAllowed) {
                    <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true"
                      aria-label="Edit document" (click)="editDocument(item)"></button>
                    <button pButton type="button" icon="pi pi-refresh" [rounded]="true" [text]="true"
                      aria-label="Renew document" (click)="editDocument(item, true)"></button>
                  }
                  @if (canVerify) {
                    <button pButton type="button" icon="pi pi-verified" [rounded]="true" [text]="true"
                      aria-label="Verify document" (click)="openVerification(item)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage"><tr><td colspan="8">No company documents have been registered.</td></tr></ng-template>
          </p-table>
        </app-page-data-panel>
      </p-tabpanel>

      <p-tabpanel value="portals">
        <app-page-data-panel title="Authority & Portal Directory"
          subtitle="Editable official entry points and operational instructions. Credentials and MFA secrets must never be stored here."
          [resultText]="authorities.length + ' authorities'">
          @if (canSetup && mutationAllowed) {
            <div actions><button pButton type="button" label="Add Portal" icon="pi pi-plus" (click)="editAuthority()"></button></div>
          }
          <p-table [value]="authorities" styleClass="p-datatable-sm" responsiveLayout="scroll"
            [tableStyle]="{ 'min-width': '900px' }">
            <ng-template pTemplate="header"><tr><th>Authority</th><th>Category</th><th>Portal</th><th>Account Reference</th><th>Instructions</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td><strong>{{ item.authorityName }}</strong><br><small>{{ item.authorityCode }} · {{ item.countryCode }}</small></td>
                <td>{{ format(item.category) }}</td>
                <td><strong>{{ item.portalName || '-' }}</strong>
                  @if (item.portalUrl) { <br><a [href]="item.portalUrl" target="_blank" rel="noopener">Open official portal</a> }
                </td>
                <td>{{ item.nonSecretAccountReference || '-' }}</td>
                <td>{{ item.instructions || '-' }}</td>
                <td>@if (canSetup && mutationAllowed) {
                  <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true"
                    aria-label="Edit portal" (click)="editAuthority(item)"></button>
                }</td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </p-tabpanel>

      <p-tabpanel value="setup">
        <section class="setup-grid">
          <app-page-data-panel title="Compliance Scopes"
            subtitle="Independent reporting units for a company, branch, warehouse, office, or facility."
            [resultText]="scopes.length + ' scopes'">
            @if (canSetup && mutationAllowed) {
              <div actions><button pButton type="button" label="Add Scope" icon="pi pi-plus" size="small" (click)="editScope()"></button></div>
            }
            <div class="scope-list">
              @for (item of scopes; track item.scopeId) {
                <button type="button" (click)="canSetup && mutationAllowed ? editScope(item) : null">
                  <span><i class="fa-solid fa-location-dot"></i><strong>{{ item.scopeName }}</strong></span>
                  <small>{{ item.scopeCode }} · {{ format(item.scopeType) }}</small>
                </button>
              } @empty { <p class="empty-copy">No compliance scopes configured.</p> }
            </div>
          </app-page-data-panel>

          <app-page-data-panel title="KSA Admin Compliance Template"
            subtitle="Versioned starter content for Qiwa, GOSI, WPS, Muqeem, transport, facilities, ZATCA, corporate registrations, and national address. Tenant edits remain owned by your company.">
            <div class="template-card">
              <i class="fa-solid fa-flag"></i>
              <div><strong>KSA Admin Compliance v1</strong><span>Asia/Riyadh · 90/60/30/7 document warnings · 7/3/1/0 obligation warnings</span></div>
            </div>
            @if (canSetup && mutationAllowed) {
              <button pButton type="button" label="Activate / Upgrade Template" icon="pi pi-sparkles"
                (click)="activateTemplate()" [disabled]="loading"></button>
            }
          </app-page-data-panel>

          <app-page-data-panel title="Spreadsheet Import"
            subtitle="Preview XLSX, XLS, or CSV rows, resolve duplicate and validation errors, then commit atomically.">
            @if (canImportExport && mutationAllowed) {
              <p-fileUpload mode="basic" chooseLabel="Choose Spreadsheet" chooseIcon="pi pi-file-excel"
                accept=".xlsx,.xls,.csv" [auto]="true" [customUpload]="true" (onSelect)="previewImport($event)"></p-fileUpload>
            }
            @if (importPreview) {
              <div class="import-summary">
                <span><strong>{{ importPreview.totalRows || 0 }}</strong>Total</span>
                <span><strong>{{ importPreview.validRows || 0 }}</strong>Valid</span>
                <span class="is-error"><strong>{{ importPreview.errorRows || 0 }}</strong>Errors</span>
                <span><strong>{{ importPreview.duplicateRows || 0 }}</strong>Duplicates</span>
              </div>
              <p-table [value]="importPreview.rows || []" styleClass="p-datatable-sm" responsiveLayout="scroll" [rows]="10" [paginator]="true">
                <ng-template pTemplate="header"><tr><th>Row</th><th>Obligation</th><th>Scope</th><th>Authority</th><th>Frequency</th><th>Validation</th></tr></ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr><td>{{ row.rowNumber }}</td><td>{{ row.obligationName }}<br><small>{{ row.obligationCode }}</small></td>
                    <td>{{ row.scopeName }}</td><td>{{ row.authorityName }}</td><td>{{ format(row.frequencyCode) }}</td>
                    <td><span [class.validation-error]="row.errors?.length">{{ row.errors?.join('; ') || 'Ready' }}</span></td></tr>
                </ng-template>
              </p-table>
              <button pButton type="button" label="Commit Validated Import" icon="pi pi-check"
                [disabled]="!!importPreview.errorRows" (click)="commitImport()"></button>
            }
          </app-page-data-panel>
        </section>
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>
</app-page-shell>

<p-dialog [(visible)]="obligationDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(980px, 96vw)' }" [header]="obligationForm.obligationId ? 'Edit Obligation' : 'New Obligation'">
  <div class="form-grid">
    <label><span>Code *</span><input pInputText [(ngModel)]="obligationForm.obligationCode"></label>
    <label class="span-2"><span>Name *</span><input pInputText [(ngModel)]="obligationForm.obligationName"></label>
    <label><span>Type *</span><p-select [options]="typeOptions" [(ngModel)]="obligationForm.obligationType"></p-select></label>
    <label><span>Frequency *</span><p-select [options]="frequencyOptions" [(ngModel)]="obligationForm.frequencyCode"></p-select></label>
    <label><span>First due date</span><input pInputText type="date" [(ngModel)]="obligationForm.firstDueDate"></label>
    <label><span>Scope *</span><p-select [options]="scopes" optionLabel="scopeName" optionValue="scopeId" [(ngModel)]="obligationForm.scopeId"></p-select></label>
    <label><span>Authority</span><p-select [options]="authorities" optionLabel="authorityName" optionValue="authorityId"
      [(ngModel)]="obligationForm.authorityId" [showClear]="true"></p-select></label>
    <label><span>Owner employee ID</span><input pInputText type="number" [(ngModel)]="obligationForm.ownerEmployeeId"></label>
    <label><span>Approver employee ID</span><input pInputText type="number" [(ngModel)]="obligationForm.approverEmployeeId"></label>
    <label><span>Confidentiality</span><p-select [options]="confidentialityOptions" [(ngModel)]="obligationForm.confidentialityLevel"></p-select></label>
    <label class="span-3"><span>Purpose</span><textarea pTextarea rows="3" [(ngModel)]="obligationForm.purpose"></textarea></label>
    <label class="check-label"><p-checkbox [binary]="true" [(ngModel)]="obligationForm.evidenceRequired"></p-checkbox><span>Evidence required</span></label>
    <label class="check-label"><p-checkbox [binary]="true" [(ngModel)]="obligationForm.approvalRequired"></p-checkbox><span>Verifier approval required</span></label>
    <label class="check-label"><p-checkbox [binary]="true" [(ngModel)]="obligationForm.active"></p-checkbox><span>Active</span></label>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="obligationDialogVisible = false"></button>
    <button pButton type="button" label="Save Obligation" icon="pi pi-check" (click)="saveObligation()"></button>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="documentDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(900px, 96vw)' }" [header]="documentForm.previousDocumentId ? 'Renew Document' : (documentForm.documentId ? 'Edit Document' : 'Add Document')">
  <div class="form-grid">
    <label class="span-2"><span>Document name *</span><input pInputText [(ngModel)]="documentForm.documentName"></label>
    <label><span>Obligation</span><p-select [options]="obligations" optionLabel="obligationName" optionValue="obligationId"
      [(ngModel)]="documentForm.obligationId" [showClear]="true"></p-select></label>
    <label><span>Document number</span><input pInputText [(ngModel)]="documentForm.documentNumber"></label>
    <label><span>Issuer</span><input pInputText [(ngModel)]="documentForm.issuer"></label>
    <label><span>Issue date</span><input pInputText type="date" [(ngModel)]="documentForm.issueDate"></label>
    <label><span>Expiry date</span><input pInputText type="date" [(ngModel)]="documentForm.expiryDate"></label>
    <label><span>File name</span><input pInputText [(ngModel)]="documentForm.fileName"></label>
    <label class="span-2"><span>File URL / evidence reference</span><input pInputText [(ngModel)]="documentForm.fileUrl"></label>
    <label><span>Confidentiality</span><p-select [options]="confidentialityOptions" [(ngModel)]="documentForm.confidentialityLevel"></p-select></label>
    <label class="span-3"><span>Notes / next action</span><textarea pTextarea rows="3" [(ngModel)]="documentForm.notes"></textarea></label>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="documentDialogVisible = false"></button>
    <button pButton type="button" label="Save Document" icon="pi pi-check" (click)="saveDocument()"></button>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="actionDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(780px, 96vw)' }" header="Compliance Action">
  <div class="cycle-context">
    <div><span>Requirement</span><strong>{{ selectedCycle?.obligationName }}</strong></div>
    <div><span>Due date</span><strong>{{ selectedCycle?.dueDate | date:'mediumDate' }}</strong></div>
    <div><span>Current status</span><p-tag [value]="format(selectedCycle?.workflowStatus)" [severity]="statusSeverity(selectedCycle?.workflowStatus)"></p-tag></div>
  </div>
  @if (selectedCycle?.evidenceRequired) {
    <section class="evidence-box">
      <h4>Required Evidence</h4>
      @for (evidence of selectedCycle?.evidence || []; track evidence.evidenceId) {
        <a [href]="evidence.fileUrl" target="_blank" rel="noopener">{{ evidence.fileName || evidence.description || 'Evidence' }}</a>
      } @empty { <small>No evidence attached yet.</small> }
      <div class="evidence-entry">
        <input pInputText placeholder="Evidence file name" [(ngModel)]="evidenceForm.fileName">
        <input pInputText placeholder="Secure file URL / reference" [(ngModel)]="evidenceForm.fileUrl">
        <button pButton type="button" label="Attach" icon="pi pi-paperclip" severity="secondary" [outlined]="true"
          (click)="addEvidence()" [disabled]="!evidenceForm.fileUrl"></button>
      </div>
    </section>
  }
  <div class="form-grid action-form">
    <label><span>Target status</span><p-select [options]="statusOptions" [(ngModel)]="actionTargetStatus"></p-select></label>
    <label class="span-2"><span>Notes</span><input pInputText [(ngModel)]="actionNotes"></label>
    @if (actionTargetStatus === 'WAIVED' || actionTargetStatus === 'NOT_APPLICABLE') {
      <label class="span-3"><span>Required reason</span><textarea pTextarea rows="3" [(ngModel)]="actionReason"></textarea></label>
    }
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="actionDialogVisible = false"></button>
    <button pButton type="button" label="Apply Controlled Transition" icon="pi pi-check" (click)="transitionCycle()"></button>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="verificationDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(600px, 96vw)' }" header="Verify Company Document">
  <div class="form-grid single-column">
    <p><strong>{{ selectedDocument?.documentName }}</strong><br><small>{{ selectedDocument?.documentNumber || 'No document number' }}</small></p>
    <label><span>Decision</span><p-select [options]="verificationOptions" [(ngModel)]="verificationStatus"></p-select></label>
    <label><span>Reason / notes</span><textarea pTextarea rows="4" [(ngModel)]="verificationReason"></textarea></label>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="verificationDialogVisible = false"></button>
    <button pButton type="button" label="Record Verification" icon="pi pi-verified" (click)="verifyDocument()"></button>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="authorityDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(800px, 96vw)' }" [header]="authorityForm.authorityId ? 'Edit Portal' : 'Add Portal'">
  <div class="form-grid">
    <label><span>Authority code *</span><input pInputText [(ngModel)]="authorityForm.authorityCode"></label>
    <label class="span-2"><span>Authority name *</span><input pInputText [(ngModel)]="authorityForm.authorityName"></label>
    <label><span>Country</span><input pInputText [(ngModel)]="authorityForm.countryCode"></label>
    <label><span>Category</span><input pInputText [(ngModel)]="authorityForm.category"></label>
    <label><span>Portal name</span><input pInputText [(ngModel)]="authorityForm.portalName"></label>
    <label class="span-2"><span>Official URL</span><input pInputText [(ngModel)]="authorityForm.portalUrl"></label>
    <label><span>Non-secret account reference</span><input pInputText [(ngModel)]="authorityForm.nonSecretAccountReference"></label>
    <label class="span-3"><span>Instructions</span><textarea pTextarea rows="3" [(ngModel)]="authorityForm.instructions"></textarea></label>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="authorityDialogVisible = false"></button>
    <button pButton type="button" label="Save Portal" icon="pi pi-check" (click)="saveAuthority()"></button>
  </ng-template>
</p-dialog>

<p-dialog [(visible)]="scopeDialogVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(700px, 96vw)' }" [header]="scopeForm.scopeId ? 'Edit Compliance Scope' : 'Add Compliance Scope'">
  <div class="form-grid">
    <label><span>Scope code *</span><input pInputText [(ngModel)]="scopeForm.scopeCode"></label>
    <label class="span-2"><span>Scope name *</span><input pInputText [(ngModel)]="scopeForm.scopeName"></label>
    <label><span>Scope type</span><p-select [options]="scopeTypeOptions" [(ngModel)]="scopeForm.scopeType"></p-select></label>
    <label><span>Existing location ID</span><input pInputText type="number" [(ngModel)]="scopeForm.locationId"></label>
    <label><span>Existing warehouse ID</span><input pInputText type="number" [(ngModel)]="scopeForm.warehouseId"></label>
    <label class="span-3"><span>Description</span><textarea pTextarea rows="3" [(ngModel)]="scopeForm.description"></textarea></label>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="scopeDialogVisible = false"></button>
    <button pButton type="button" label="Save Scope" icon="pi pi-check" (click)="saveScope()"></button>
  </ng-template>
</p-dialog>
